import React, { useState, useEffect, useCallback, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import { Combobox, Option, OptionGroup } from '@fluentui/react-components';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { withError, withValidation, AdapterFactory } from '@bingads-webui-react/validation';
import { UiBlocker } from '@bingads-webui-fluent-component/ui-blocker';
import { Pagination } from '@bingads-webui-grid/campaign-details-list';
import { withControlErrorHandling, withUnvalidatedControlErrors } from '@bingads-webui-campaign-react/error-handling';
import { loadCategoryList/* , loadCategoryNameList */ } from '@bingads-webui-campaign-react/search-campaign-data';
import { getCategorySelectorStyles } from './styles/category-selector-style';
import { ListHeader } from './list-header';
import { ListItem } from './list-item';
import { InputSlot } from './input-slot';

const pageSize = 100;

const convertOrderby = (orderby) => {
  if (_.isEmpty(orderby)) {
    return undefined;
  }

  const key = _.keys(orderby)[0];
  const dir = orderby[key];
  return `${key} ${dir > 0 ? 'asc' : 'desc'}`;
};

export const CategorySelector = ({
  preloadedCategories,
  onChange: onCategoryChange,
  domainName,
  languageCode,
  disabledCategories,
  value,
}) => {
  const i18n = useContext(I18nContext);
  const classes = useDefaultStyles(getCategorySelectorStyles);
  const [isLoading, setLoading] = useState(false);
  const [defaultCategories, setDefaultCategories] = useState(preloadedCategories || []);
  const [parentCategory, setParentCategory] = useState('');
  const [categories, setCategories] = useState(defaultCategories || []);
  const [orderby, setOrderby] = useState('');
  const [pageNumber, setPageNumber] = useState(1);
  const [pageTotal, setPageTotal] = useState(preloadedCategories.totalCount || 0);

  const onPageChange = useCallback((number, e) => {
    setPageNumber(number);
    e.stopPropagation();
  }, []);

  const pager = useMemo(() => ({
    pageNumber,
    pageSize,
    total: pageTotal,
    availablePageSizes: [pageSize],
    onPageChange,
    disablePageSizeControl: true,
  }), [onPageChange, pageNumber, pageTotal]);

  useEffect(() => {
    if (!preloadedCategories) {
      loadCategoryList(domainName, languageCode).then((response) => {
        setDefaultCategories(response.totalCount ? response : []);
        setPageTotal(response.totalCount || 0);
        if (!value) {
          setCategories(response);
        }
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // only load once

  const updateCategories = useCallback((domain, language, category, keyword, sort, skip) => {
    loadCategoryList(domain, language, category, keyword, sort, skip).then((response) => {
      let newCategories = response;
      if (window.e2eTestEnvironment) {
        newCategories = [{ Bid: 0, CategoryName: 'browse/', Coverage: 0.1334 }];
        newCategories.totalCount = 1;
      }

      setCategories(newCategories.totalCount ? newCategories : []);
      setPageTotal(newCategories.totalCount || 0);
      setLoading(false);
    });
  }, []);

  const debouncedUpdateCategories = useMemo(
    () => _.debounce(updateCategories, 1000),
    [updateCategories]
  );

  useEffect(() => {
    setLoading(true);
    if (pageNumber === 1) {
      debouncedUpdateCategories(domainName, languageCode, parentCategory, value, convertOrderby(orderby));
    } else {
      setPageNumber(1);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, parentCategory, orderby]);

  useEffect(() => {
    setLoading(true);
    debouncedUpdateCategories(domainName, languageCode, parentCategory, value, convertOrderby(orderby), (pageNumber - 1) * pageSize);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNumber]);

  const onInputChange = useCallback((e) => {
    onCategoryChange(e.target.value, false);
  }, [onCategoryChange]);

  const onOptionSelect = useCallback((e, data) => {
    if (data.optionValue !== 'listheader' && data.optionValue !== 'listfooter') {
      onCategoryChange(data.optionText, true);
    }
  }, [onCategoryChange]);

  const onChangeScope = useCallback((category) => {
    setParentCategory(category);
  }, []);

  const onSortChange = useCallback((field) => {
    const newOrderby = { [field]: orderby[field] === 1 ? -1 : 1 };
    setOrderby(newOrderby);
  }, [orderby]);

  return (
    <Combobox
      placeholder={i18n.getString(_TL_('Search all categories'))}
      onChange={onInputChange}
      onOptionSelect={onOptionSelect}
      className={classes.root}
      value={value}
      inlinePopup
      input={{
        children: InputSlot,
      }}
    >
      <UiBlocker blocking={isLoading} loadingText={i18n.getString(_TL_('Search...'))} className={classes.blocker} >
        <Option key="listHeader" value="listheader" text={i18n.getString(_TL_('List heaer'))} role="heading" disabled aria-level={1} aria-label={i18n.getString(_TL_('Use the left and right keys to access the buttons in the list'))} >
          <ListHeader parentCategory={parentCategory} onChangeScope={onChangeScope} orderby={orderby} onSortChange={onSortChange} />
        </Option>
        <OptionGroup>
          {categories.map(({ CategoryName, Coverage }) => (
            <Option key={CategoryName} text={CategoryName} disabled={!_.isUndefined(disabledCategories[CategoryName])} >
              <ListItem name={CategoryName} size={Coverage} onChangeScope={onChangeScope} />
            </Option>
          ))}
        </OptionGroup>
        {
          pageTotal > categories.length && (
            <Option key="listFooter" value="listfooter" text={i18n.getString(_TL_('List footer'))} disabled role="navigation" className={classes.footer} >
              <div className={classes.pager}><Pagination {...pager} /></div>
            </Option>
        )}
      </UiBlocker>
    </Combobox>
  );
};

CategorySelector.propTypes = {
  preloadedCategories: PropTypes.arrayOf(PropTypes.shape({})),
  disabledCategories: PropTypes.arrayOf(PropTypes.string),
  // preloadedCategoryNames: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func,
  domainName: PropTypes.string.isRequired,
  languageCode: PropTypes.string,
  value: PropTypes.string.isRequired,
};

CategorySelector.defaultProps = {
  preloadedCategories: null,
  onChange: _.noop,
  // preloadedCategoryNames: null,
  languageCode: 'en',
  disabledCategories: [],
};

const Adapter = AdapterFactory({ convertEventToValue: value => value });
export const ValidatedCategorySelector = withControlErrorHandling(withUnvalidatedControlErrors(withError(withValidation(CategorySelector, Adapter))));
